import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

type AuditRow = {
  ts: string;
  provider: string;
  model: string;
  caller: string;
  inputTokens?: number;
  outputTokens?: number;
  estimatedCostUsd?: number | string;
  status: string;
  errorMessage?: string;
  /** Tool names MCP fired in the agentic loop adjacent to this LLM call. */
  tools?: string[];
};

const numFmt = new Intl.NumberFormat();

function fmtCost(v: AuditRow["estimatedCostUsd"]): string {
  const n = typeof v === "string" ? Number(v) : v;
  if (n == null || Number.isNaN(n)) return "—";
  if (n === 0) return "$0";
  return n < 0.01 ? `$${n.toFixed(4)}` : `$${n.toFixed(2)}`;
}

/**
 * Caller renders. Background sub-calls (narrate / rewrite) come back
 * tagged `system:*` so they don't show as the bare "unknown" they used
 * to. A genuinely empty caller is still possible on older rows — render
 * it as a muted "system" pill rather than the raw word, which reads as
 * an outage when it's just an un-attributed background call.
 */
function CallerCell({ value }: { value: string | undefined | null }) {
  const v = (value ?? "").trim();
  const isSystem = v.startsWith("system:") || v === "unknown" || v === "";
  if (!isSystem) return <span className="font-medium">{v}</span>;
  const label = v === "" || v === "unknown"
    ? "system"
    : v.replace(/^system:/, "system · ");
  return (
    <span
      className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600"
      title="Background sub-call (e.g. follow-up rewrite, result narration). Not directly triggered by a user message."
    >
      {label}
    </span>
  );
}

function ToolsCell({ tools }: { tools: string[] | undefined }) {
  if (!tools || tools.length === 0) {
    return <span className="text-slate-400">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {tools.map((t) => (
        <span
          key={t}
          className="inline-flex items-center rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-emerald-200"
          title={`MCP tool called: ${t}`}
        >
          {t}
        </span>
      ))}
    </div>
  );
}

export function AuditPage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["audit"],
    queryFn: async () => (await api.get<AuditRow[]>("/audit")).data,
  });

  if (isError) {
    return (
      <div className="mx-auto max-w-5xl p-6">
        <p role="alert" className="rounded bg-red-50 p-3 text-sm text-red-900">
          Admin access required to view the audit log.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-xl font-semibold">AI audit log</h1>
      {isLoading ? (
        <p className="mt-4 text-sm">Loading…</p>
      ) : (
        <div data-tour-id="audit-table" className="mt-4 overflow-x-auto rounded border border-datumcraft-slate/20 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-datumcraft-mist text-left text-xs uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2">Time</th>
                <th className="px-3 py-2">Provider</th>
                <th className="px-3 py-2">Model</th>
                <th className="px-3 py-2">Caller</th>
                <th className="px-3 py-2">Tools called</th>
                <th className="px-3 py-2 text-right">Input</th>
                <th className="px-3 py-2 text-right">Output</th>
                <th className="px-3 py-2 text-right">Cost (USD)</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Error</th>
              </tr>
            </thead>
            <tbody>
              {(data ?? []).map((r, i) => (
                <tr key={i} className="border-t border-datumcraft-slate/10">
                  <td className="px-3 py-1.5">{new Date(r.ts).toLocaleString()}</td>
                  <td className="px-3 py-1.5">{r.provider}</td>
                  <td className="px-3 py-1.5">{r.model}</td>
                  <td className="px-3 py-1.5"><CallerCell value={r.caller} /></td>
                  <td className="px-3 py-1.5"><ToolsCell tools={r.tools} /></td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {r.inputTokens != null ? numFmt.format(r.inputTokens) : "—"}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {r.outputTokens != null ? numFmt.format(r.outputTokens) : "—"}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtCost(r.estimatedCostUsd)}</td>
                  <td className="px-3 py-1.5">{r.status}</td>
                  <td className="px-3 py-1.5 text-red-700">{r.errorMessage}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
